import { useMemo, useRef } from 'react';

import { Card } from '@components/common/Card';
import { Container } from '@components/common/Container';
import { Icon } from '@components/Icon';
import { LandingCardDeck } from '@components/features/landing/LandingCardDeck';
import { useClassDeposit } from '@hooks/useClassDeposit';

import { photos } from '@assets/photos';

const SECTION_DESCRIPTION =
  "Meeting once a week on Thursdays (two 12-week semesters), BHG offers academic and enrichment classes. As we do all things for God's glory with excellence, classes will challenge students to grow both academically and socially.";

function buildPillars(depositFormatted: string, hasDeposit: boolean) {
  const depositSentence = hasDeposit
    ? `A ${depositFormatted} nonrefundable deposit per class is paid at sign-up to secure your child's spot; tuition is paid directly to the tutors.`
    : 'Payment for classes is made directly to the tutors.';

  return [
    {
      id: 'education',
      title: 'Education that aligns with the Bible',
      paragraphs: [
        'We provide two 12-week semesters yearly, offering academic and enrichment classes. All knowledge comes from Christ, therefore, all classes point students back to their Creator and Savior. As we do all things for God\u2019s glory with excellence, classes will challenge kids to grow both academically and socially. Tutors will provide a syllabus and communicate academic and character expectations beforehand concerning classwork and homework requirements.',
        'Our tutors are BHG Board approved adults who are actively pursuing the Lord in their daily lives. They have been background checked. Our tutors desire for your children to grow academically and socially in their classrooms.',
        depositSentence,
      ],
      icon: 'bible' as const,
      color: 'blue' as const,
      photo: photos.teensWork,
      alt: 'Teens working together around a table',
    },
    {
      id: 'socialization',
      title: 'Socialization that aligns with the Bible',
      paragraphs: [
        'At Bethlehem Homeschool Group, we value loving God and loving others just as Jesus commanded (Mark 12:28-31). We love others by making friends, serving one another, respecting people and property, and building each other up in the Lord. Students and guardians strive to love God and others inside and outside the classroom.',
        'BHG encourages serving others at our group through helping clean-up in the lunch hall, helping in classrooms as needed, walking hallways for safety, leading and planning social events and field trips, etc.',
        'In addition, BHG members know that parents are the primary teacher, disciple-maker, and authority and, therefore, are ultimately responsible for their children\u2019s behavior and discipline. We look to the Disciplinary Action Plan to guide tutors and families, and help in classroom management strategies.',
        'Since parents are the authority in their children\u2019s lives we are NOT a drop-off program for students until age 15. As children grow in character and maturity alongside one another, their parents come alongside them at BHG. This allows parents to have opportunities to \u201cconsider how to stir up one another to love and good works\u201d (Hebrews 10:24).',
      ],
      icon: 'heart-handshake' as const,
      color: 'green' as const,
      photo: photos.outdoorTeacher,
      alt: 'Teacher and students outdoors with plants',
    },
    {
      id: 'support',
      title: 'Encourage and support families',
      paragraphs: [
        'Our group provides multiple Bible studies and/or book studies with a Biblical perspective to help each other grow in our relationships with the Lord. As our children watch us learn and grow, they are inspired to learn and grow themselves! These studies also give us an opportunity to mentor or be mentored as we lead our children in their education.',
        'Throughout the year we will also have training opportunities by believers in a variety of homeschooling or discipleship topics. We desire for all families to have the support and help they need as they take on the task of discipling their children.',
        'As we are not the body of Christ, we encourage each family to be involved in their local Bible-believing church. We encourage any family to attend other events at Bethlehem Baptist Church, but know ultimately that worshipping and serving together in a local body of believers is of far more value eternally for your family. God has designed us to live in community together as the church.',
      ],
      icon: 'users-group' as const,
      color: 'orange' as const,
      photo: photos.familyFlower,
      alt: 'Family in a field of flowers',
    },
  ];
}

function pillarCardClass(isSelected: boolean, isDimmed: boolean): string {
  return [
    'bhg-landing-pillar-card',
    'bhg-landing-card-deck__card',
    isSelected ? 'bhg-landing-card-deck__card--selected' : '',
    isDimmed ? 'bhg-landing-card-deck__card--dimmed' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function WhatIsBHG() {
  const sectionRef = useRef<HTMLElement>(null);
  const { depositFormatted, hasDeposit } = useClassDeposit();
  const pillars = useMemo(
    () => buildPillars(depositFormatted, hasDeposit),
    [depositFormatted, hasDeposit]
  );

  return (
    <section
      id="about"
      ref={sectionRef}
      className="bhg-landing-section"
      aria-labelledby="about-heading"
    >
      <div className="bhg-landing-section__background">
        <div className="bhg-landing-section__overlay" />
      </div>
      <Container size="lg">
        <div className="bhg-landing-section__header">
          <h2 id="about-heading" className="bhg-landing-section__title">
            What is BHG?
          </h2>
          <p className="bhg-landing-section__description">{SECTION_DESCRIPTION}</p>
        </div>

        <LandingCardDeck
          items={pillars}
          rowClassName="bhg-landing-pillars"
          sectionRef={sectionRef}
          detailPanelId="about-pillar-detail"
          inlineDetailMaxWidth={1024}
          renderCard={(item, { isSelected, isDimmed, onSelect, onKeyDown }) => {
            const pillar = pillars.find((p) => p.id === item.id)!;
            return (
              <Card
                elevation={3}
                interactive
                padding={false}
                className={pillarCardClass(isSelected, isDimmed)}
                role="button"
                tabIndex={0}
                aria-expanded={isSelected}
                aria-controls="about-pillar-detail"
                aria-label={`${pillar.title}. ${isSelected ? 'Showing full text below' : 'Read more'}`}
                onClick={onSelect}
                onKeyDown={onKeyDown}
              >
                <div className="bhg-landing-pillar-card__image-wrapper">
                  <img
                    src={pillar.photo.src}
                    srcSet={pillar.photo.srcSet}
                    sizes={pillar.photo.cardSizes}
                    alt={pillar.alt}
                    className="bhg-landing-pillar-card__image"
                    loading="lazy"
                    decoding="async"
                    width={400}
                    height={250}
                  />
                  <div className="bhg-landing-pillar-card__icon">
                    <Icon
                      type={pillar.icon}
                      style="filled"
                      colorVariant={pillar.color}
                      size="xl"
                      aria-hidden
                    />
                  </div>
                </div>
                <div className="bhg-landing-pillar-card__content">
                  <h3 className="bhg-landing-pillar-card__title">{pillar.title}</h3>
                  <p className="bhg-landing-pillar-card__description">{pillar.paragraphs[0]}</p>
                  <p className="bhg-landing-pillar-card__cta" aria-hidden>
                    {isSelected ? 'Showing below' : 'Read more'}
                  </p>
                </div>
              </Card>
            );
          }}
          renderDetail={(item, { onClose }) => {
            const pillar = pillars.find((p) => p.id === item.id)!;
            return (
              <Card elevation={3} padding={false} className="bhg-landing-pillar-detail">
                <div className="bhg-landing-pillar-detail__header">
                  <div className="bhg-landing-pillar-detail__heading">
                    <Icon
                      type={pillar.icon}
                      style="filled"
                      colorVariant={pillar.color}
                      size="md"
                      aria-hidden
                    />
                    <h3 className="bhg-landing-pillar-detail__title">{pillar.title}</h3>
                  </div>
                  <button
                    type="button"
                    className="bhg-landing-card-deck__close"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    Close
                  </button>
                </div>
                <div className="bhg-landing-pillar-detail__body">
                  {pillar.paragraphs.map((paragraph) => (
                    <p key={paragraph.slice(0, 24)} className="bhg-landing-pillar-detail__paragraph">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </Card>
            );
          }}
        />
      </Container>
    </section>
  );
}
